/**
 * 승인 큐 — 파트너 마스터 신청(EA-11~EA-13). 회원가입 승인(EA-08~EA-10)은 2026-10-12(물음 16 ⑤ — 가입 때 KYC)에 「KYC
 * verification」 메뉴(KycVerification.tsx)로 바뀌었다 — 보통 가입은 신원 인증이 통과하면 저절로 승인되고, 관리자는 수동심사만.
 *
 * 계약: XM-34 `status=PENDING`(offset)·XM-36·XM-37.
 *
 * ⚠ **버튼은 키로 그린다**(명세 0-5). 목록은 보기 키 `partner_applications.view`, 승인·거절은
 * `partner_applications.review`.
 *
 * ⚠ **파트너 거절은 사유가 필수다**(XM-37, 명세 EA-13 고침).
 *
 * 2026-09-30 고침(api-XB XM-34·XM-36): 파트너 신청 줄에 고른 단계(`tier` — 확인 31)와 계약 생성
 * 가스료 송금(`gas` — 확인 20·21·24·57: 신청 즉시 0.02 USDE, 환전풀로)을 보인다. 가스료가 확인
 * (`CONFIRMED`)되기 전에는 서버가 승인을 409 `application_gas_unpaid` 로 막는다 — 화면도 「승인」을
 * 잠그고 까닭을 적는다. 옛 신청(`gas` 없음)은 잠그지 않는다(서버가 판단). 가스료는 거절돼도
 * 돌려주지 않는다(확인 55).
 *
 * 2026-09-30 검토 고침(문구 — 03 15번 제안): 승인 창의 옛 문구 「첫 보증금이 확인되면 … 열립니다.
 * 기한 안에 첫 보증금이 없으면 승인이 취소됩니다.」는 새 규칙과 어긋났다 — 활성은 넣은 합 ≥ 고른
 * 단계 금액(확인 14), 기한 안에 못 채우면 반환 후 일반회원(원문 24, 확인 30 Q-36), 이미 낸 달
 * 사용료는 돌려주지 않음(확인 18 — 미납 포함)이라 「남은 보증금」. 거절 창에는 가스료를 이미 낸
 * 신청(`CONFIRMED`)에만 '돌려주지 않는다'를 적는다(확인 55).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { approvePartnerApplication, listPartnerApplications, rejectPartnerApplication } from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { Button, Card, Code, Labeled, Maybe, MemberLink, Notice, TD, TextArea, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { ApiError } from '@/api/errors'
import { EnglishOnlyNote, hasKorean, REASON_MAIL_HINT } from '@/components/EnglishOnly'
import { QueryState } from '@/components/QueryState'
import { OffsetPager } from '@/components/Pager'
import { useFilters } from '@/components/Filters'
import { formatTime, usde } from '@/format'
import { GRADE_LABEL, type PartnerApplication } from '@/api/types'
import { tierText } from './Collateral'

/**
 * EA-12 승인 창 안내 — 활성은 넣은 합 ≥ 고른 단계 금액(확인 14), 기한 안에 못 채우면 남은 보증금을
 * 돌려주고 일반회원(원문 24, 확인 30 Q-36, 확인 18). 문구는 제안(03 15번).
 */
export const APPROVE_NOTE =
  'Once approved, this member becomes a partner master and a collateral contract is created. When the total collateral deposited reaches the amount of the chosen tier, the referral code and the capacity open. If the chosen tier amount is not filled by the deadline, the remaining collateral is returned and the member becomes a general member.'

/** EA-13 거절 창 안내. 가스료를 낸 신청이면 뒤에 '돌려주지 않습니다'(확인 55)를 붙인다. */
export const REJECT_NOTE = 'Rejects the application. The grade stays the same.'

/** 가스료가 아직 확인되지 않아 승인을 잠가야 하는가(api-XB XM-36 409 `application_gas_unpaid`). */
export function gasBlocksApproval(row: PartnerApplication): boolean {
  return row.gas !== undefined && row.gas !== null && row.gas.status !== 'CONFIRMED'
}

const LIMIT = 25
const NO_FILTERS: readonly string[] = []

/* ── EA-11 파트너 신청 승인 (XM-34) ──────────────────────────────── */

export function PartnerApplicationsPage() {
  const f = useFilters(NO_FILTERS)
  const params = { status: 'PENDING', offset: f.offset, limit: LIMIT }
  const q = useQuery({
    queryKey: ['partner-applications', params],
    queryFn: () => listPartnerApplications(params),
  })
  const rows = q.data ?? []
  // 발견-627: 결정 창은 줄 밖에 둔다 — 409 뒤 대기 목록을 다시 읽어 그 줄이 빠져도 창과 창 안 빨간 글이 남는다.
  const [deciding, setDeciding] = useState<{ row: PartnerApplication; mode: 'approve' | 'reject' } | null>(null)
  // 다시 읽은 대기 목록에 없으면(다른 사람이 먼저 처리) 창의 확인 단추를 잠근다.
  const stillPending = deciding !== null && rows.some((r) => r.applicationId === deciding.row.applicationId)

  return (
    <Card title="Partner applications">
      <p className="mb-4 text-xs text-slate-500">
        Partner master applications waiting for approval. Approving makes the applicant a partner master.
      </p>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="No applications are waiting for approval.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>ID</th><th className={TH}>Grade when applied</th>
            <th className={TH}>Chosen tier</th><th className={TH}>Gas fee</th>
            <th className={TH}>Terms version</th><th className={TH}>Agreed at</th>
            <th className={TH}>Applied at</th><th className={TH}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.applicationId}>
                <td className={TD}><MemberLink memberId={r.memberId} loginId={r.loginId} /></td>
                <td className={TD}><Labeled labels={GRADE_LABEL} code={r.gradeAtApply} /></td>
                <td className={TD}><Maybe value={tierText(r.tier)} /></td>
                <td className={TD}>
                  {r.gas
                    ? <>
                        <span className="font-mono">{usde(r.gas.amount)}</span> <Code>{r.gas.status}</Code>
                        {r.gas.confirmedAt !== null && <div className="text-xs text-slate-500">{formatTime(r.gas.confirmedAt)}</div>}
                      </>
                    : <Maybe value={null} />}
                </td>
                <td className={TD}><Maybe value={r.termsVersion} /></td>
                <td className={TD}><Time ms={r.termsAgreedAt} /></td>
                <td className={TD}><Time ms={r.appliedAt} /></td>
                <td className={TD}><PartnerDecisionButtons onOpen={(mode) => setDeciding({ row: r, mode })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <OffsetPager offset={f.offset} limit={LIMIT} count={q.data?.length} onChange={f.setOffset} />
      {deciding && (
        <PartnerDecisionDialog row={deciding.row} mode={deciding.mode} decided={!stillPending}
          onClose={() => setDeciding(null)} />
      )}
    </Card>
  )
}

/** EA-11 줄의 [Approve]·[Reject] — 누르면 EA-12·EA-13 창을 연다. */
function PartnerDecisionButtons({ onOpen }: { onOpen: (mode: 'approve' | 'reject') => void }) {
  const { has } = useSession()
  // 버튼 키 `partner_applications.review`(명세 0-5, 계약 7절).
  if (!has('partner_applications.review')) return null
  return (
    <div className="flex gap-2">
      <Button onClick={() => onOpen('approve')}>Approve</Button>
      <Button tone="plain" onClick={() => onOpen('reject')}>Reject</Button>
    </div>
  )
}

/** EA-12 파트너 마스터 승인 창 · EA-13 파트너 신청 거절 창. */
function PartnerDecisionDialog({ row, mode, decided, onClose }: {
  row: PartnerApplication
  mode: 'approve' | 'reject'
  /** 다시 읽은 대기 목록에 이 신청이 없다(발견-627 — 다른 사람이 먼저 처리). */
  decided: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const w = useWrite()

  function close() { onClose() }

  async function submit() {
    const approve = mode === 'approve'
    const why = reason.trim()
    const send = async () => {
      if (approve) {
        const r = await approvePartnerApplication(row.applicationId)
        setFlash({
          tone: 'ok',
          text: `Approved ${row.loginId} as a partner master. First collateral deadline: `
            + (formatTime(r.firstDepositDeadline) ?? '—'),
        })
        await qc.invalidateQueries({ queryKey: ['partners'] })
      } else {
        await rejectPartnerApplication(row.applicationId, why)
        setFlash({ tone: 'ok', text: `Rejected the partner application of ${row.loginId}.` })
      }
      await qc.invalidateQueries({ queryKey: ['partner-applications'] })
    }
    await submitWrite(w, approve ? 'Approve partner' : 'Reject partner', send, close, {
      // 발견-627: 409(already_reviewed — 다른 사람이 먼저 처리 등)면 화면이 낡았다 — 대기 목록을 다시 읽는다. 창은 남긴다.
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          void qc.invalidateQueries({ queryKey: ['partner-applications'] })
        }
      },
    })
  }

  return (
    <>
      <Confirm
        open
        title={mode === 'approve' ? 'Approve partner master' : 'Reject partner application'}
        action={mode === 'approve' ? 'Approve' : 'Reject'}
        danger={mode === 'approve'}
        busy={w.busy} error={w.error} onCancel={close} onConfirm={() => { void submit() }}
        disabled={decided || (mode === 'reject' && (reason.trim() === '' || hasKorean(reason)))
          || (mode === 'approve' && gasBlocksApproval(row))}
      >
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <dt className="text-slate-500">ID</dt><dd>{row.loginId}</dd>
          <dt className="text-slate-500">Chosen tier</dt><dd><Maybe value={tierText(row.tier)} /></dd>
          <dt className="text-slate-500">Gas fee</dt>
          <dd>{row.gas ? <>{usde(row.gas.amount)} <Code>{row.gas.status}</Code></> : <Maybe value={null} />}</dd>
        </dl>
        {mode === 'approve' && gasBlocksApproval(row) && (
          <div className="mt-3">
            <Notice tone="warn">The gas fee transfer is not confirmed yet, so this cannot be approved.</Notice>
          </div>
        )}
        <p className="mt-3 text-xs text-slate-500">
          {mode === 'approve'
            ? APPROVE_NOTE
            : REJECT_NOTE + (row.gas?.status === 'CONFIRMED'
              ? ` The gas fee of ${usde(row.gas.amount) ?? '—'} paid with the application is not returned.`
              : '')}
        </p>
        {mode === 'reject' && (
          <div className="mt-3 flex flex-col gap-1">
            <TextArea label="Rejection reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
            {/* 사유는 회원에게 영어 메일로 간다(사장님 2026-10-02 「일단 영어로만」) — 한글이면 [Reject] 잠금(서버도 거절). */}
            <EnglishOnlyNote texts={[reason]} hint={REASON_MAIL_HINT}
              blocked="The reason has Korean letters. [Reject] stays off until it is written in English only." />
          </div>
        )}
      </Confirm>
    </>
  )
}
